import { useDomainRepository } from "hooks";
import { MerchantDetailTab } from "./types";
import useFetchResource from "hooks/useFetchResource";
import { PER_PAGE_SIZE } from "consts/transactions";
import {
    createMerchantsMethodsPivot,
    getMerchantsMethodsPivot,
    getMerchantsMethodsPivotHistory,
    updateMerchantsMethodsPivot
} from "features/pivots/api";
import {
    createOrUpdateMerchantBusiness,
    createOrUpdateMerchantDomain,
    createOrUpdatePayoutAccount,
    createOrUpdatePayoutCryptoAccount,
    deleteMerchantBusiness,
    deleteMerchantDomain,
    deletePayoutAccount,
    deletePayoutCryptoAccount,
    getMerchantBusinessById,
    getMerchantBusinesses,
    getMerchantDomains,
    getPayoutAccounts,
    getPayoutCryptoAccounts
} from "features/merchants/api";
import { getMerchantPayoutIncrementTaxes } from "features/merchantPayouts/api";
import { RequestQueryMapper } from "util/request-query-mapper";
import { Paginate } from "consts/table";
import { AccountType, Filters } from "consts/merchants";
import useMerchantDetail from "./useMerchantDetail";
import { useRepository } from "./repository";
import { useSimpleRequest, type RequestSettings } from "hooks/useRequest";
import { getAccountType } from "features/merchants/helpers";
import type { Merchant, MerchantBusiness, MerchantDomain, MerchantPayoutAbstractAccount } from "features/merchants/types";
import type { MerchantProfileFinance } from "ui/forms/Merchant/ProfileFinance";
// MerchantProfile
import {
    reducer as merchantProfileReducer,
    stateFactory as merchantProfileStateFactory,
    Repository as MerchantProfileRepository,
    MerchantProfileActionType,
    type MerchantProfileRepositoryState
} from "./MerchantProfile/repository";
// MerchantUsers
import {
    reducer as merchantUsersReducer,
    stateFactory as merchantUsersStateFactory,
    Repository as MerchantUsersRepository,
    MerchantUsersActionType,
    type MerchantUsersRepositoryState
} from "./MerchantUsers/repository";
// MerchantAccountSettings
import {
    reducer as merchantAccountSettingsReducer,
    stateFactory as merchantAccountSettingsStateFactory,
    Repository as MerchantAccountSettingsRepository,
    MerchantAccountSettingsActionType,
    type MerchantAccountSettingsRepositoryState
} from "./MerchantAccountSettings/repository";
// MerchantPaymentMethods
import {
    reducer as merchantPaymentMethodsReducer,
    stateFactory as merchantPaymentMethodsStateFactory,
    Repository as MerchantPaymentMethodsRepository,
    MerchantPaymentMethodsActionType,
    type MerchantPaymentMethodsRepositoryState,
    type CreateMerchantMethodPivot,
    type FetchMerchantsMethodsHistory,
    type UpdateMerchantsMethodsPivot
} from "./MerchantPaymentMethods/repository";
// MerchantFeesAndCommisions
import {
    reducer as merchantFeesAndCommisionsReducer,
    stateFactory as merchantFeesAndCommisionsStateFactory,
    Repository as MerchantFeesAndCommisionsRepository,
    MerchantFeesAndCommisionsActionType,
    type MerchantFeesAndCommisionsRepositoryState
} from "./MerchantFeesAndCommisions/repository";
// MerchantDomains
import {
    reducer as merchantDomainsReducer,
    stateFactory as merchantDomainsStateFactory,
    Repository as MerchantDomainsRepository,
    MerchantDomainsActionType,
    type MerchantDomainsRepositoryState
} from "./MerchantDomains/repository";

export default function useMerchantDetailRepository() {
    const fetchResourceSettings = {
        shouldFetchAll: true,
        perPage: PER_PAGE_SIZE
    };

    const getPayoutAccountsRequest = useFetchResource(fetchResourceSettings);
    const getPayoutCryptoAccountsRequest = useFetchResource(fetchResourceSettings);
    const getMerchantsMethodsPivotRequest = useFetchResource(fetchResourceSettings);
    const getMerchantsMethodsPivotHistoryRequest = useFetchResource({
        ...fetchResourceSettings,
        perPage: 10
    });
    const getMerchantDomainsRequest = useFetchResource(fetchResourceSettings);
    const getMerchantBusinessesRequest = useFetchResource(fetchResourceSettings);
    const getMerchantPayoutIncrementTaxesRequest = useFetchResource(fetchResourceSettings);

    const request = useSimpleRequest();

    const { merchant } = useMerchantDetail();

    const merchantDetailRepository = useRepository(merchant);

    return new Map()
        .set(MerchantDetailTab.Profile, useDomainRepository<
            MerchantProfileRepositoryState,
            MerchantProfileActionType
        >({
            reducer: merchantProfileReducer,
            initializerArg: merchantProfileStateFactory(),
            factory: (state, dispatch) => new MerchantProfileRepository(
                state,
                dispatch,
                merchantDetailRepository.inject(),
                () => getPayoutAccountsRequest((queryParams?: string) => getPayoutAccounts(
                    RequestQueryMapper.from(queryParams)
                        .contains(Filters.merchantId, String(merchant.coreId))
                        .toString()
                )),
                () => getPayoutCryptoAccountsRequest((queryParams?: string) => getPayoutCryptoAccounts(
                    RequestQueryMapper.from(queryParams)
                        .contains(Filters.merchantId, String(merchant.coreId))
                        .toString()
                )),
                (account: MerchantProfileFinance) => request<MerchantPayoutAbstractAccount>(() =>
                    new Map()
                        .set(AccountType.Bank, createOrUpdatePayoutAccount)
                        .set(AccountType.Crypto, createOrUpdatePayoutCryptoAccount)
                        .get(getAccountType(account))({
                            ...account,
                            merchantId: merchant.coreId
                        })
                    , { shouldNotifyOnSuccess: true }),
                (account: MerchantPayoutAbstractAccount) => request(() =>
                    new Map()
                        .set(AccountType.Bank, createOrUpdatePayoutAccount)
                        .set(AccountType.Crypto, createOrUpdatePayoutCryptoAccount)
                        .get(getAccountType(account))(account)
                ),
                (account: MerchantPayoutAbstractAccount) => request(() =>
                    new Map()
                        .set(AccountType.Bank, deletePayoutAccount)
                        .set(AccountType.Crypto, deletePayoutCryptoAccount)
                        .get(getAccountType(account))(account.coreId)
                    , { shouldNotifyOnSuccess: true })
            )
        }))
        .set(MerchantDetailTab.Users, useDomainRepository<
            MerchantUsersRepositoryState,
            MerchantUsersActionType
        >({
            reducer: merchantUsersReducer,
            initializerArg: merchantUsersStateFactory(),
            factory: (state, dispatch) => new MerchantUsersRepository(
                state,
                dispatch,
                merchantDetailRepository.inject()
            )
        }))
        .set(MerchantDetailTab.AccountSettings, useDomainRepository<
            MerchantAccountSettingsRepositoryState,
            MerchantAccountSettingsActionType
        >({
            reducer: merchantAccountSettingsReducer,
            initializerArg: merchantAccountSettingsStateFactory(),
            factory: (state, dispatch) => new MerchantAccountSettingsRepository(
                state,
                dispatch,
                merchantDetailRepository.inject()
            )
        }))
        .set(MerchantDetailTab.PaymentMethods, useDomainRepository<
            MerchantPaymentMethodsRepositoryState,
            MerchantPaymentMethodsActionType
        >({
            reducer: merchantPaymentMethodsReducer,
            initializerArg: merchantPaymentMethodsStateFactory(),
            factory: (state, dispatch) => new MerchantPaymentMethodsRepository(
                state,
                dispatch,
                () => getMerchantsMethodsPivotRequest((queryParams?: string) => getMerchantsMethodsPivot(
                    RequestQueryMapper.from(queryParams)
                        .containsIn(Filters.merchantId, String(merchant.coreId))
                        .toString()
                )),
                ({ methodId, page }: FetchMerchantsMethodsHistory) => getMerchantsMethodsPivotHistoryRequest((queryParams?: string) => getMerchantsMethodsPivotHistory(
                    RequestQueryMapper.from(queryParams)
                        .contains(Filters.merchantId, String(merchant.coreId))
                        .contains(Filters.methodId, String(methodId))
                        .contains(Paginate.page, String(page))
                        .toString()
                )),
                (merchantsMethodsPivot: CreateMerchantMethodPivot) => request(() => createMerchantsMethodsPivot(merchantsMethodsPivot), {
                    shouldNotifyOnSuccess: true
                }),
                (merchantsMethodsPivot: UpdateMerchantsMethodsPivot) => request(() =>
                    updateMerchantsMethodsPivot(merchantsMethodsPivot)
                )
            )
        }))
        .set(MerchantDetailTab.FeeAndCommissions, useDomainRepository<
            MerchantFeesAndCommisionsRepositoryState,
            MerchantFeesAndCommisionsActionType
        >({
            reducer: merchantFeesAndCommisionsReducer,
            initializerArg: merchantFeesAndCommisionsStateFactory(),
            factory: (state, dispatch) => new MerchantFeesAndCommisionsRepository(
                state,
                dispatch,
                merchantDetailRepository.inject(),
                () => getMerchantPayoutIncrementTaxesRequest((queryParams?: string) => getMerchantPayoutIncrementTaxes(
                    RequestQueryMapper.from(queryParams)
                        .contains(Filters.merchantId, String(merchant.coreId))
                        .toString()
                ))
            )
        }))
        .set(MerchantDetailTab.Domains, useDomainRepository<
            MerchantDomainsRepositoryState,
            MerchantDomainsActionType
        >({
            reducer: merchantDomainsReducer,
            initializerArg: merchantDomainsStateFactory(),
            factory: (state, dispatch) => new MerchantDomainsRepository(
                state,
                dispatch,
                (requestQueryMapper: RequestQueryMapper) => getMerchantDomainsRequest((queryParams: string = '') => getMerchantDomains(
                    requestQueryMapper
                        .concat(queryParams)
                        .toString()
                )),
                (merchant?: Partial<Merchant>) => getMerchantBusinessesRequest((queryParams?: string) => getMerchantBusinesses(
                    merchant?.coreId
                        ? RequestQueryMapper.from(queryParams)
                            .containsIn(Filters.merchantId, String(merchant.coreId))
                            .toString()
                        : queryParams
                )),
                (merchantBusinessId: MerchantBusiness['coreId']) => request(() => getMerchantBusinessById(merchantBusinessId)),
                (merchantBusiness: Partial<MerchantBusiness>, requestSettings?: RequestSettings) => request(() =>
                    createOrUpdateMerchantBusiness(merchantBusiness),
                    requestSettings
                ),
                (merchantDomain: Partial<MerchantDomain>, requestSettings?: RequestSettings) => request(() =>
                    createOrUpdateMerchantDomain(merchantDomain),
                    requestSettings
                ),
                (merchantBusinessId: MerchantBusiness['coreId']) => request(() =>
                    deleteMerchantBusiness(merchantBusinessId),
                    { shouldNotifyOnSuccess: true }
                ),
                (merchantDomainId: MerchantDomain['coreId']) => request(() =>
                    deleteMerchantDomain(merchantDomainId),
                    { shouldNotifyOnSuccess: true }
                )
            )
        }));
};
